<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import dealStatusAPI from 'dashboard/api/pipeline/dealStatus';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import {
  formatEditableNumber,
  formatLocalizedCurrency,
  normalizeCurrencyCode,
  normalizeLocale,
  parseLocalizedNumber,
} from '../utils/localeFormatting';
import {
  getCardOpportunitiesCount,
  getCardRealizedValue,
} from '../helpers/columnFilterMatchers';
import PipelineProductsAPI from 'dashboard/api/pipeline/products';
import { multiplicarDinheiro } from '../helpers/decimalMoney';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);
const { t, locale } = useI18n();
const store = useStore();
const { showAlert } = useAlert();
const { isCloudFeatureEnabled } = useAccount();

// Fase 59 — Oportunidades é cross feature opt-in. Desligada, este modal volta a
// ser o de antes dela: valor livre, sem catálogo, sem razão de vendas. O
// fechamento em si nunca depende da feature.
const isOpportunitiesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PIPELINE_OPPORTUNITIES)
);

const submitting = ref(false);
const selectedOfferIndex = ref(null);
const wonValue = ref('');
const wonNote = ref('');
const valueTouched = ref(false);
const submitError = ref('');
const wonValueInputRef = ref(null);

const offers = computed(() => {
  const raw = props.item?.item_details?.offers;
  return Array.isArray(raw) ? raw : [];
});

const accountCurrency = computed(() => {
  const account = store.getters.getCurrentAccount || {};
  return account.currency || account.settings?.currency || 'BRL';
});

const currencyCode = rawCurrency => {
  const hasExplicitCurrency =
    rawCurrency !== undefined && rawCurrency !== null && rawCurrency !== '';
  const raw = hasExplicitCurrency
    ? rawCurrency
    : props.item.item_details?.currency || accountCurrency.value;
  return normalizeCurrencyCode(raw);
};

const selectedCurrencyCode = computed(() => {
  const selectedOffer = offers.value[selectedOfferIndex.value];
  return currencyCode(selectedOffer?.currency);
});

const itemTitle = computed(
  () =>
    props.item.item_details?.title ||
    props.item.title ||
    t('PIPELINE.UNTITLED_ITEM')
);

// Fase 59 — o card pode já ter venda no razão (foi ganho, reaberto e está sendo
// ganho de novo). Confirmar aqui grava uma venda NOVA, não corrige a anterior:
// o razão é append-only de propósito, e cards de recorrência vendem várias
// vezes. Sem dizer isso, quem reabriu para consertar um valor errado dobrava a
// receita sem perceber — o card mostra o último valor, o total soma os dois.
const recordedSalesCount = computed(() =>
  isOpportunitiesEnabled.value ? getCardOpportunitiesCount(props.item) : 0
);
const alreadyRecorded = computed(() => recordedSalesCount.value > 0);
const recordedTotalLabel = computed(() => {
  const total = getCardRealizedValue(props.item);
  if (total === null || total === undefined) return '';
  return (
    formatLocalizedCurrency(total, {
      currency: currencyCode(props.item.currency),
      locale: locale.value,
    }) || String(total)
  );
});

// ── Catálogo (Fase 59/F3) ────────────────────────────────────────────────────
//
// Fechar deixa de ser digitar um número solto e passa a ser escolher O QUE foi
// vendido, com o preço já cadastrado. É isso que liga a venda ao produto no
// razão — sem produto, ela não entra em nenhuma medição por produto.
//
// O catálogo é OPCIONAL: conta sem produtos (ou funil sem produto disponível)
// continua fechando por valor livre, exatamente como antes.
const catalog = ref([]);
const catalogLoading = ref(false);
const catalogFailed = ref(false);
// Fase 59/F5 — uma venda tem N linhas de item, não um produto só. Cada linha
// carrega o próprio produto, quantidade e unitário; o total da venda é a soma
// das linhas, calculado no SERVIDOR a partir do array (mandar o total junto só
// criaria uma terceira versão do número para discordar das outras duas).
//
// `chave` existe porque `pipeline_product_id` NÃO identifica a linha: o mesmo
// produto pode aparecer duas vezes na mesma venda (dois períodos, dois
// descontos), e usar o id do produto como `:key` faria o Vue reciclar o DOM da
// linha errada ao remover uma delas.
let proximaChaveDeItem = 0;
const saleItems = ref([]);

// Teto do servidor (`SaleItemsParams::MAX_ITEMS`). Travar aqui evita montar uma
// venda de 51 linhas para o servidor recusar inteira no fim.
const MAX_SALE_ITEMS = 50;

// A moeda é do PRODUTO: formatar tudo na moeda da conta exibiria um item em USD
// como se fosse real — mesmo número, significado outro.
const formatMoney = (value, currency) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  return (
    formatLocalizedCurrency(number, {
      currency: currencyCode(currency),
      locale: locale.value,
    }) || String(number)
  );
};

// A moeda do razão é congelada POR CARD — o modelo recusa duas moedas no mesmo
// card. Oferecer um produto em USD num card em BRL só levaria a uma recusa de
// validação (ou, pior, a um número certo com significado errado), então ele nem
// aparece como opção.
const cardCurrency = computed(() => currencyCode(props.item?.currency));
const selectableCatalog = computed(() =>
  catalog.value.filter(
    product => currencyCode(product.currency) === cardCurrency.value
  )
);
// Produtos existem, mas nenhum na moeda deste card: dizer isso é melhor que
// mostrar um catálogo vazio que parece "conta sem produtos".
const catalogHiddenByCurrency = computed(
  () => catalog.value.length > 0 && selectableCatalog.value.length === 0
);

const hasCatalog = computed(() => selectableCatalog.value.length > 0);

// Produto de uma linha. Sempre resolvido contra `selectableCatalog`, NUNCA
// contra `catalog`: é esse recorte que impede uma linha em USD entrar num card
// em BRL. Se o produto sumir do recorte (troca de moeda do card com o modal
// aberto), a linha fica sem produto e o envio trava, em vez de mandar um
// vínculo que o servidor recusaria com 422.
const productOfItem = item =>
  selectableCatalog.value.find(p => p.id === item.productId) || null;

const hasSaleItems = computed(() => saleItems.value.length > 0);
const canAddMoreItems = computed(() => saleItems.value.length < MAX_SALE_ITEMS);

// Fase 59/F4 — com catálogo disponível, a venda é DE ALGUMA COISA. Um valor
// solto fica fora de qualquer medição por produto, e o razão é append-only: não
// há como vincular depois. Por isso, havendo o que escolher, escolher deixa de
// ser opcional.
//
// Sem catálogo — conta que ainda não cadastrou nada, funil sem produto na moeda
// do card, ou catálogo que falhou ao carregar — o valor livre continua sendo o
// caminho: travar o fechamento de um negócio já ganho seria pior que registrar
// a venda sem vínculo.
const missingProduct = computed(() => hasCatalog.value && !hasSaleItems.value);

// Teto do `per_page` do backend. O endpoint é paginado: uma página só deixaria
// o produto 201 impossível de escolher, e a venda dele seria registrada sem
// vínculo — exatamente o buraco que a F3 existe para fechar.
const CATALOG_PAGE_SIZE = 200;

// Teto para desistir do catálogo. O negócio JÁ foi ganho: um pedido pendurado
// não pode impedir de registrá-lo para sempre. Estourado o prazo, o modal cai
// no valor livre com o aviso de falha — pior que vincular, melhor que travar.
const CATALOG_TIMEOUT_MS = 10000;

let catalogRequestId = 0;

const loadCatalog = async () => {
  catalogRequestId += 1;
  const atual = catalogRequestId;
  const cardDoPedido = props.item?.id;
  // A CONTA também entra no guard: o modal é persistente e o mesmo id de card
  // existe em inquilinos diferentes — só o id deixaria a resposta de uma conta
  // pintar o catálogo de outra.
  const contaDoPedido = store.getters.getCurrentAccountId;
  const aindaVale = () =>
    atual === catalogRequestId &&
    cardDoPedido === props.item?.id &&
    contaDoPedido === store.getters.getCurrentAccountId;
  catalogLoading.value = true;
  catalogFailed.value = false;
  const prazo = setTimeout(() => {
    if (!aindaVale()) return;
    catalog.value = [];
    catalogFailed.value = true;
    catalogLoading.value = false;
    // Invalida o pedido: se ele responder depois, não pode ressuscitar a lista
    // debaixo de um operador que já está fechando por valor livre.
    catalogRequestId += 1;
  }, CATALOG_TIMEOUT_MS);
  try {
    const coletados = [];
    let offset = 0;
    let total = null;
    do {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await PipelineProductsAPI.list({
        activeOnly: true,
        pipelineId: props.item?.pipeline_id ?? undefined,
        perPage: CATALOG_PAGE_SIZE,
        offset,
      });
      // Revalida a CADA página: entre uma e outra a conta pode ter mudado, e
      // misturar páginas de inquilinos diferentes é pior que descartar tudo.
      if (!aindaVale()) return;
      const pagina = Array.isArray(data?.products) ? data.products : [];
      coletados.push(...pagina);
      total = Number(data?.meta?.total_count);
      offset += CATALOG_PAGE_SIZE;
      // Página vazia encerra: um total_count inconsistente viraria laço infinito.
      if (!pagina.length) break;
    } while (Number.isFinite(total) && coletados.length < total);

    if (!aindaVale()) return;
    catalog.value = coletados;
    catalogLoading.value = false;
  } catch {
    // Catálogo indisponível NÃO bloqueia o fechamento: o negócio foi ganho e
    // precisa ser registrado. Mas a tela DIZ que falhou — silenciar viraria
    // "esta conta não tem catálogo", e o operador registraria sem vínculo uma
    // venda que o razão nunca mais deixa corrigir.
    if (!aindaVale()) return;
    catalog.value = [];
    catalogFailed.value = true;
    catalogLoading.value = false;
  } finally {
    clearTimeout(prazo);
  }
};

const parseWonValue = raw => {
  const input = String(raw ?? '').trim();
  if (!input) return null;

  const localized = parseLocalizedNumber(input, locale.value, {
    currency: selectedCurrencyCode.value,
  });
  if (localized !== null) return localized;

  const canonical = Number(input);
  return Number.isFinite(canonical) ? canonical : null;
};

const hasValueError = computed(() => {
  const input = String(wonValue.value ?? '').trim();
  if (!input) return false;
  const parsed = parseWonValue(input);
  return parsed === null || parsed < 0;
});

// ── Quantidade × unitário (só com produto escolhido) ─────────────────────────
//
// O operador edita o UNITÁRIO, não o total: assim `quantidade × unitário` bate
// sempre com o total gravado. Deixar editar o total permitiria salvar 2 × 3.000
// somando 5.500 — três números que se contradizem na mesma linha do razão.
// Cada linha é parseada por si. Uma linha inválida não pode derrubar as outras
// da tela: o operador precisa VER qual delas está errada, não perder as três.
const parseItemQuantity = raw => {
  const texto = String(raw ?? '').trim();
  if (!texto) return null;
  const parsed = parseWonValue(texto);
  if (parsed === null || !Number.isFinite(parsed)) return null;
  // Quantidade 0 não é venda — é o `CHECK (quantity > 0)` da tabela.
  return parsed > 0 ? parsed : null;
};

const parseItemUnitValue = raw => {
  const texto = String(raw ?? '').trim();
  if (!texto) return null;
  const parsed = parseWonValue(texto);
  return parsed !== null && parsed >= 0 ? parsed : null;
};

// A linha resolvida: produto, números parseados e o total dela. É a ÚNICA
// fonte que o resto do componente lê — template, total e envio olham para cá,
// então o que está na tela e o que vai para o servidor não têm como divergir.
const resolvedItems = computed(() =>
  saleItems.value.map(item => {
    const product = productOfItem(item);
    const quantity = parseItemQuantity(item.quantity);
    const unitValue = parseItemUnitValue(item.unitValue);
    // Conta em inteiros, não em float: o servidor compara em decimal exato e
    // recusaria um total que o navegador arredondou de outro jeito — um 422 que
    // o operador não teria como corrigir, já que o número na tela é o rejeitado.
    const total =
      quantity === null || unitValue === null
        ? null
        : multiplicarDinheiro(quantity, unitValue);
    return {
      chave: item.chave,
      produto: product,
      quantity,
      unitValue,
      total,
      // Sem produto a linha é inválida mesmo com números bons: seria uma venda
      // sem vínculo, que é o buraco que o catálogo existe para fechar.
      invalida:
        !product || quantity === null || unitValue === null || total === null,
    };
  })
);

const hasItemErrors = computed(() =>
  resolvedItems.value.some(item => item.invalida)
);

// Total da venda = soma das linhas. `null` se qualquer linha não fecha: exibir
// a soma parcial das linhas válidas anunciaria um valor que não é o da venda.
const itemsTotal = computed(() => {
  if (!hasSaleItems.value || hasItemErrors.value) return null;
  return resolvedItems.value.reduce((soma, item) => soma + item.total, 0);
});

const itemsTotalLabel = computed(() => {
  if (itemsTotal.value === null) return '—';
  return (
    formatLocalizedCurrency(itemsTotal.value, {
      currency: cardCurrency.value,
      locale: locale.value,
    }) || String(itemsTotal.value)
  );
});

const itemTotalLabel = item => {
  if (item.total === null) return '—';
  return (
    formatLocalizedCurrency(item.total, {
      currency: cardCurrency.value,
      locale: locale.value,
    }) || String(item.total)
  );
};

// A moeda do card no rótulo: o modal do arrasto mostrava o símbolo e, sem ele,
// não se sabe em que moeda o valor livre está sendo digitado — num card em USD
// o operador digitaria pensando em real.
const freeValueLabel = computed(
  () => `${t('PIPELINE_MODALS.WON_VALUE_LABEL')} (${cardCurrency.value})`
);

function addSaleItem(id) {
  if (submitting.value || !canAddMoreItems.value) return;

  const product = selectableCatalog.value.find(p => p.id === id);
  if (!product) return;

  proximaChaveDeItem += 1;
  saleItems.value.push({
    chave: proximaChaveDeItem,
    productId: id,
    quantity: '1',
    // Preço do catálogo como ponto de partida, não como camisa de força: o
    // desconto (ou o acréscimo) daquela negociação se digita por cima.
    //
    // Formatado pelo locale, não `String(default_value)`: o valor cru
    // ("3000.0") ficaria ao lado de um total e de um preço de catálogo já
    // formatados. `formatEditableNumber` e NÃO `formatLocalizedCurrency`
    // porque o campo é de EDIÇÃO — o símbolo não se redigita num teclado
    // `inputmode="decimal"`. O ida-e-volta está coberto no spec.
    unitValue:
      product.default_value === null || product.default_value === undefined
        ? ''
        : formatEditableNumber(product.default_value, locale.value),
  });
}

function removeSaleItem(chave) {
  if (submitting.value) return;
  saleItems.value = saleItems.value.filter(item => item.chave !== chave);
}

// Reabrir o modal começa do zero: as linhas do fechamento anterior não podem
// reaparecer num card diferente.
function clearProduct() {
  saleItems.value = [];
  proximaChaveDeItem = 0;
}

function selectOffer(index) {
  if (submitting.value) return;

  selectedOfferIndex.value = index;
  const offer = offers.value[index];
  if (offer?.value !== undefined && offer?.value !== null) {
    wonValue.value = String(offer.value);
    valueTouched.value = false;
  }
}

watch(
  // O modal é persistente: em PipelineColumn/CardDetails ele continua montado e
  // só troca de `item`. Observando apenas `show`, o catálogo do card anterior
  // ficava na tela E selecionável — e, entre contas, de outro inquilino.
  () => [props.show, props.item?.id, store.getters.getCurrentAccountId],
  ([visible]) => {
    submitting.value = false;
    valueTouched.value = false;
    submitError.value = '';
    selectedOfferIndex.value = null;
    wonValue.value = '';
    wonNote.value = '';
    catalog.value = [];
    clearProduct();

    // Sem a cross feature o catálogo nem é buscado: a rota responde 403, e
    // pedir para receber uma negativa só encheria o console de erro.
    if (visible && isOpportunitiesEnabled.value) loadCatalog();
    if (!visible) return;

    // Valor PREVISTO como ponto de partida — quem arrasta o card para Ganho
    // quase sempre fecha pelo valor que já estava na negociação. Digitar de
    // novo um número que o card já tem é trabalho que a tela pode poupar.
    const previsto =
      props.item?.expected_revenue ?? props.item?.item_details?.value;
    if (previsto !== null && previsto !== undefined && previsto !== '') {
      wonValue.value = String(previsto);
    }

    if (!offers.value.length) return;

    const defaultIndex = offers.value.findIndex(offer => offer?.is_default);
    let index = null;
    if (defaultIndex >= 0) index = defaultIndex;
    else if (offers.value.length === 1) index = 0;
    if (index !== null) selectOffer(index);
  },
  { immediate: true }
);

function formatOfferValue(offer) {
  if (offer?.value === undefined || offer?.value === null) return '';
  const currency = currencyCode(offer.currency);
  if (currency) {
    return (
      formatLocalizedCurrency(offer.value, {
        currency,
        locale: locale.value,
      }) || String(offer.value)
    );
  }

  const number = Number(offer.value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat(normalizeLocale(locale.value)).format(number)
    : String(offer.value);
}

// O eco do servidor bate com o que foi pedido? Só os ids, em ordem: quantidade
// e valor o servidor pode normalizar legitimamente (2 vira 2.0), e comparar
// isso geraria alarme onde não há problema. O que não pode mudar é O QUE foi
// vendido.
function itensConferem(itensGravados) {
  if (!Array.isArray(itensGravados)) return false;

  const pedidos = resolvedItems.value.map(item => item.produto.id);
  const gravados = itensGravados.map(item => item.pipeline_product_id);
  if (pedidos.length !== gravados.length) return false;

  return pedidos.every((id, indice) => Number(gravados[indice]) === Number(id));
}

async function handleConfirm() {
  if (submitting.value) return;
  // Confirmar ANTES de o catálogo chegar registraria, de forma irreversível,
  // uma venda sem produto — com o produto certo aparecendo um segundo depois.
  if (catalogLoading.value) return;

  valueTouched.value = true;
  if (hasSaleItems.value && hasItemErrors.value) return;
  // Só valida o valor livre quando ele é o que está em uso: com itens na venda
  // o campo está oculto, e tentar focá-lo travaria a confirmação sem exibir
  // erro nenhum.
  if (!hasSaleItems.value && hasValueError.value) {
    await nextTick();
    wonValueInputRef.value?.$el?.querySelector('input')?.focus();
    return;
  }

  // Com produto escolhido, o valor fechado É quantidade × unitário — o mesmo
  // número que o operador está vendo antes de confirmar.
  // O card em que o fechamento COMEÇOU. Trocar de card (ou de conta) com o
  // pedido em voo faria a resposta ser aplicada ao card errado — e o PipelineTab
  // mesclaria uma venda alheia sobre ele.
  const cardDoEnvio = props.item?.id;
  const contaDoEnvio = store.getters.getCurrentAccountId;
  const rawValue = String(wonValue.value ?? '').trim();
  const parsedValue = hasSaleItems.value
    ? itemsTotal.value
    : rawValue && parseWonValue(rawValue);

  // O array que vai no corpo. NÃO mandamos `total_value` por linha: o servidor
  // deriva de `quantity * unit_value` e, mandando os três, qualquer divergência
  // de arredondamento entre navegador e servidor vira 422 numa venda correta.
  const itensDoEnvio = hasSaleItems.value
    ? resolvedItems.value.map(item => ({
        pipelineProductId: item.produto.id,
        // O nome do produto vai como `title` da linha porque o histórico rotula
        // TODA linha por `title` — é a convenção que as linhas já gravadas
        // seguem, inclusive as de catálogo. O payload de leitura devolve
        // `pipeline_product_id`, não o nome, então sem isto a venda nova
        // apareceria no histórico como uma linha sem nome.
        title: item.produto.name,
        quantity: String(item.quantity),
        unitValue: String(item.unitValue),
      }))
    : undefined;
  submitting.value = true;
  submitError.value = '';
  try {
    const { data } = await dealStatusAPI.markAsWon(props.item.id, {
      wonValue:
        parsedValue === null || parsedValue === false || parsedValue === ''
          ? ''
          : String(parsedValue),
      wonNote: wonNote.value,
      winningOfferIndex: selectedOfferIndex.value,
      items: itensDoEnvio,
    });

    if (
      cardDoEnvio !== props.item?.id ||
      contaDoEnvio !== store.getters.getCurrentAccountId
    ) {
      return;
    }

    // Compara o que foi PEDIDO com o que o servidor diz ter gravado. Num deploy
    // rolling, uma versão antiga aceita os itens, ignora e responde sucesso — e
    // a venda fica sem vínculo para sempre, porque o razão é append-only.
    //
    // Compara o ARRAY, não o `pipeline_product_id` do cabeçalho: numa venda com
    // itens o cabeçalho é NULL por definição (o produto vive nas linhas), então
    // a comparação antiga alarmaria FALSO em toda venda — inclusive nas que
    // gravaram certo. Compara os ids em ORDEM, e não como conjunto, porque duas
    // linhas do mesmo produto são legítimas e um conjunto as fundiria.
    if (hasSaleItems.value && !itensConferem(data?.opportunity?.items)) {
      showAlert(t('PIPELINE.DEAL_STATUS.ERROR.PRODUCT_NOT_LINKED'), 'error');
    } else {
      showAlert(t('PIPELINE.DEAL_STATUS.SUCCESS.MARKED_WON'));
    }
    emit('success', data);
    emit('close');
  } catch (error) {
    // Mesmo guard do sucesso: um erro do pedido ANTIGO não pode aparecer no
    // card novo — e nem apagar o `submitting` de um envio que ainda está em voo.
    if (
      cardDoEnvio !== props.item?.id ||
      contaDoEnvio !== store.getters.getCurrentAccountId
    ) {
      return;
    }

    // 409 é a recusa antifraude do servidor: o card já está fechado e o valor
    // de um negócio fechado não se altera em silêncio. Dizer isso — e o
    // caminho (reabrir) — vale mais que "falha ao atualizar status".
    submitError.value =
      error?.response?.status === 409
        ? t('PIPELINE.DEAL_STATUS.ERROR.ALREADY_WON')
        : t('PIPELINE.DEAL_STATUS.ERROR.MARK_FAILED');
    showAlert(submitError.value, 'error');
  } finally {
    if (
      cardDoEnvio === props.item?.id &&
      contaDoEnvio === store.getters.getCurrentAccountId
    ) {
      submitting.value = false;
    }
  }
}

function handleCancel() {
  if (!submitting.value) emit('close');
}
</script>

<template>
  <Modal
    :show="show"
    :close-on-backdrop-click="!submitting"
    :close-on-escape="!submitting"
    :show-close-button="!submitting"
    initial-focus-selector="input[name='winning-offer'], #mark-won-value-input"
    @close="handleCancel"
  >
    <section
      class="flex max-h-[min(90vh,48rem)] w-full flex-col overflow-hidden"
      :aria-busy="submitting"
      data-modal="mark-as-won"
    >
      <header class="px-4 pt-5 sm:px-6 sm:pt-6">
        <h2 class="mb-2 pr-10 text-heading-2 text-n-slate-12">
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.TITLE') }}
        </h2>
        <p class="text-body-main text-n-slate-11">
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.MESSAGE') }}
        </p>
      </header>

      <div class="overflow-y-auto px-4 py-5 sm:px-6">
        <div class="mb-5 rounded-lg border border-n-teal-9 bg-n-teal-3 p-4">
          <p class="break-words text-label font-medium text-n-teal-11">
            {{ itemTitle }}
          </p>
        </div>

        <!-- Card que JÁ vendeu: confirmar soma outra venda, não substitui a
             anterior. Quem reabriu para corrigir precisa saber disso ANTES de
             confirmar — depois, a receita já está dobrada. -->
        <p
          v-if="alreadyRecorded"
          class="mb-5 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
          role="status"
          data-testid="already-recorded"
        >
          {{
            t('PIPELINE.DEAL_STATUS.WON_MODAL.ALREADY_RECORDED', {
              count: recordedSalesCount,
              total: recordedTotalLabel,
            })
          }}
        </p>

        <fieldset v-if="offers.length" class="mb-5">
          <legend class="mb-2 text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.SELECT_OFFER') }}
          </legend>
          <div class="max-h-48 space-y-2 overflow-y-auto">
            <label
              v-for="(offer, index) in offers"
              :key="index"
              class="flex min-h-12 cursor-pointer items-start gap-3 rounded-lg border border-n-weak p-3 transition-colors hover:bg-n-alpha-1"
              :class="{
                'border-n-teal-9 bg-n-teal-3': selectedOfferIndex === index,
              }"
            >
              <input
                :id="`winning-offer-${index}`"
                :checked="selectedOfferIndex === index"
                type="radio"
                name="winning-offer"
                :disabled="submitting"
                class="mt-0.5 size-5 shrink-0 accent-n-teal-9"
                @change="selectOffer(index)"
              />
              <span class="min-w-0 flex-1">
                <span class="block break-words text-label text-n-slate-12">
                  {{
                    offer.description ||
                    t('PIPELINE.DEAL_STATUS.WON_MODAL.OFFER_UNTITLED', {
                      index: index + 1,
                    })
                  }}
                </span>
                <span
                  class="mt-1 block text-label-small font-semibold text-n-teal-11"
                >
                  {{ formatOfferValue(offer) }}
                </span>
                <span
                  v-if="offer.is_default"
                  class="mt-1 inline-block rounded-full bg-n-amber-3 px-2 py-0.5 text-xs text-n-amber-11"
                >
                  {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.DEFAULT_OFFER') }}
                </span>
              </span>
            </label>
          </div>
        </fieldset>

        <!-- Enquanto o catálogo carrega, o formulário de valor livre sozinho
             diria "esta conta não vende nada" — e o operador registraria sem
             vínculo uma venda que o razão não deixa mais corrigir. -->
        <p
          v-if="catalogLoading"
          class="mb-5 rounded-lg bg-n-alpha-1 px-4 py-3 text-sm text-n-slate-11"
          role="status"
        >
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.CATALOG_LOADING') }}
        </p>

        <p
          v-else-if="catalogFailed"
          class="mb-5 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
          role="status"
        >
          {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.CATALOG_FAILED') }}
        </p>

        <p
          v-else-if="catalogHiddenByCurrency"
          class="mb-5 rounded-lg bg-n-amber-3 px-4 py-3 text-sm text-n-amber-11"
          role="status"
        >
          {{
            t('PIPELINE.DEAL_STATUS.WON_MODAL.CATALOG_OTHER_CURRENCY', {
              currency: cardCurrency,
            })
          }}
        </p>

        <!-- Fase 59/F5 — a venda tem N linhas. O catálogo deixou de ser uma
             escolha única (radio) e virou a lista de onde se ADICIONA cada
             linha; o mesmo produto pode entrar duas vezes, com quantidades e
             descontos diferentes. Só aparece quando há catálogo disponível
             NESTE funil e NESTA moeda; sem catálogo, o fechamento por valor
             livre continua igual ao de antes. -->
        <fieldset v-if="hasCatalog" class="mb-5">
          <legend class="mb-2 text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.SELECT_PRODUCT') }}
          </legend>
          <div class="max-h-40 space-y-2 overflow-y-auto">
            <button
              v-for="product in selectableCatalog"
              :key="product.id"
              type="button"
              name="won-product"
              class="flex min-h-12 w-full items-center gap-3 rounded-lg border border-n-weak p-3 text-left transition-colors hover:bg-n-alpha-1 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="submitting || !canAddMoreItems"
              :data-product-id="product.id"
              @click="addSaleItem(product.id)"
            >
              <span
                class="i-lucide-plus size-5 shrink-0 text-n-teal-11"
                aria-hidden="true"
              />
              <span class="min-w-0 flex-1">
                <span class="block break-words text-label text-n-slate-12">
                  {{ product.name }}
                </span>
                <span
                  v-if="product.category"
                  class="block text-label-small text-n-slate-10"
                >
                  {{ product.category }}
                </span>
              </span>
              <span
                class="shrink-0 text-label-small font-semibold tabular-nums text-n-teal-11"
              >
                {{
                  product.default_value === null ||
                  product.default_value === undefined ||
                  product.default_value === ''
                    ? t('PIPELINE.DEAL_STATUS.WON_MODAL.NO_PRICE')
                    : formatMoney(product.default_value, product.currency)
                }}
              </span>
            </button>
          </div>
          <!-- Havendo catálogo, escolher não é opcional: o botão de confirmar
               fica travado até aqui, e sem dizer por quê ele pareceria quebrado.
               Tom NEUTRO, não de erro: é instrução, e o `role="status"` (não
               `alert`) já dizia isso. -->
          <p
            v-if="missingProduct"
            class="mt-2 text-label-small text-n-slate-11"
            role="status"
          >
            {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.PRODUCT_REQUIRED') }}
          </p>
          <p
            v-else-if="!canAddMoreItems"
            class="mt-2 text-label-small text-n-amber-11"
            role="status"
          >
            {{
              t('PIPELINE.DEAL_STATUS.WON_MODAL.ITEMS_LIMIT', {
                count: MAX_SALE_ITEMS,
              })
            }}
          </p>
        </fieldset>

        <!-- As linhas da venda. Cada uma com a própria quantidade e o próprio
             unitário, EDITÁVEIS — o desconto daquela linha se digita por cima
             do preço de catálogo. -->
        <ul v-if="hasSaleItems" class="mb-4 flex list-none flex-col gap-3 pl-0">
          <li
            v-for="(linha, indice) in saleItems"
            :key="linha.chave"
            class="rounded-lg border border-n-weak p-3"
            data-testid="won-item"
          >
            <div class="mb-2 flex items-start justify-between gap-3">
              <span class="min-w-0 break-words text-label text-n-slate-12">
                {{ resolvedItems[indice].produto?.name || '' }}
              </span>
              <Button
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                :disabled="submitting"
                :aria-label="
                  t('PIPELINE.DEAL_STATUS.WON_MODAL.REMOVE_ITEM', {
                    name: resolvedItems[indice].produto?.name || '',
                  })
                "
                @click="removeSaleItem(linha.chave)"
              />
            </div>
            <div class="flex flex-wrap items-start gap-3">
              <div class="w-24 [&_input]:!h-12">
                <Input
                  v-model="linha.quantity"
                  type="text"
                  name="won-quantity"
                  inputmode="decimal"
                  autocomplete="off"
                  :disabled="submitting"
                  :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.QUANTITY_LABEL')"
                  :message="
                    valueTouched && resolvedItems[indice].quantity === null
                      ? t('PIPELINE.ERRORS.VALUE_INVALID')
                      : ''
                  "
                  :message-type="
                    valueTouched && resolvedItems[indice].quantity === null
                      ? 'error'
                      : 'info'
                  "
                  :aria-invalid="
                    valueTouched && resolvedItems[indice].quantity === null
                  "
                  @blur="valueTouched = true"
                />
              </div>
              <div class="min-w-32 flex-1 [&_input]:!h-12">
                <Input
                  v-model="linha.unitValue"
                  type="text"
                  name="won-unit-value"
                  inputmode="decimal"
                  autocomplete="off"
                  :disabled="submitting"
                  :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.UNIT_VALUE_LABEL')"
                  :message="
                    valueTouched && resolvedItems[indice].unitValue === null
                      ? t('PIPELINE.ERRORS.VALUE_INVALID')
                      : ''
                  "
                  :message-type="
                    valueTouched && resolvedItems[indice].unitValue === null
                      ? 'error'
                      : 'info'
                  "
                  :aria-invalid="
                    valueTouched && resolvedItems[indice].unitValue === null
                  "
                  @blur="valueTouched = true"
                />
              </div>
              <div
                class="flex flex-col justify-center"
                data-testid="won-item-total"
              >
                <span class="text-label-small uppercase text-n-slate-10">
                  {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.TOTAL_LABEL') }}
                </span>
                <span class="text-label font-bold tabular-nums text-n-slate-12">
                  {{ itemTotalLabel(resolvedItems[indice]) }}
                </span>
              </div>
            </div>
          </li>
        </ul>

        <!-- Total da venda: a soma das linhas, que é o número que vai para o
             razão. Fica separado da linha para não se confundir com o total DE
             UMA linha quando há várias. -->
        <div
          v-if="hasSaleItems"
          class="mb-4 flex items-baseline justify-between gap-3 rounded-lg bg-n-alpha-1 px-3 py-2"
          data-testid="won-total"
        >
          <span class="text-label-small uppercase text-n-slate-10">
            {{ t('PIPELINE.DEAL_STATUS.WON_MODAL.SALE_TOTAL_LABEL') }}
          </span>
          <span
            class="text-heading-2 font-bold tabular-nums text-n-teal-11"
            aria-live="polite"
          >
            {{ itemsTotalLabel }}
          </span>
        </div>

        <div v-if="!hasSaleItems" class="mb-4 [&_input]:!h-12">
          <Input
            id="mark-won-value-input"
            ref="wonValueInputRef"
            v-model="wonValue"
            type="text"
            name="won-value"
            inputmode="decimal"
            autocomplete="off"
            :disabled="submitting"
            :label="freeValueLabel"
            :placeholder="t('PIPELINE_MODALS.VALUE_PLACEHOLDER')"
            :message="
              valueTouched && hasValueError
                ? t('PIPELINE.ERRORS.VALUE_INVALID')
                : ''
            "
            :message-type="valueTouched && hasValueError ? 'error' : 'info'"
            :aria-invalid="valueTouched && hasValueError"
            @blur="valueTouched = true"
          />
        </div>

        <TextArea
          id="mark-won-note-textarea"
          v-model="wonNote"
          :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_NOTE_LABEL')"
          :placeholder="
            t('PIPELINE.DEAL_STATUS.WON_MODAL.WON_NOTE_PLACEHOLDER')
          "
          :disabled="submitting"
          auto-height
          min-height="4rem"
        />

        <p
          v-if="submitError"
          class="mt-4 rounded-lg bg-n-ruby-3 p-3 text-sm text-n-ruby-11"
          role="alert"
        >
          {{ submitError }}
        </p>
      </div>

      <footer
        class="flex flex-col-reverse gap-2 border-t border-n-weak px-4 py-4 sm:flex-row sm:px-6"
      >
        <Button
          variant="outline"
          color="slate"
          size="lg"
          :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.CANCEL')"
          :disabled="submitting"
          class="w-full flex-1"
          @click="handleCancel"
        />
        <Button
          color="teal"
          size="lg"
          :label="t('PIPELINE.DEAL_STATUS.WON_MODAL.CONFIRM')"
          :is-loading="submitting || catalogLoading"
          :disabled="submitting || catalogLoading || missingProduct"
          class="w-full flex-1"
          @click="handleConfirm"
        />
      </footer>
    </section>
  </Modal>
</template>
